import { ReactNode, SyntheticEvent, useMemo, useState } from 'react';
import Highlighter from 'react-highlight-words';
import cn from 'classnames';
import { ReactComponent as ChevronDown } from 'images/newIcons/chevron-down.svg';
import { ReactComponent as StarActiveIcon } from 'images/newIcons/starActive.svg';
import { ReactComponent as StarPassiveIcon } from 'images/newIcons/starPassive.svg';
import { ISelectOption } from 'interfaces';

interface SelectItemProps<T, P> {
  value: T | T[];
  handleClick: (value: T, option: ISelectOption<T, P>) => void;
  option: ISelectOption<T, P>;
  searchValue: string;
  depth: number;
  isExpandable: boolean;
  enableFavorites?: boolean;
  toggleFavorite?: (value: string, isFavorite: boolean) => void;
  idx?: number;
  renderSubOption?: (
    depth: number
  ) => (option: ISelectOption, idx: number) => ReactNode;
  isMultiple?: boolean;
}

const SelectItem = <T, P>({
  value,
  handleClick,
  option,
  enableFavorites,
  searchValue,
  depth,
  isExpandable,
  toggleFavorite,
  idx,
  renderSubOption,
  isMultiple,
}: SelectItemProps<T, P>) => {
  const [isExpanded, setExpanded] = useState(false);

  const showChevron = isExpandable && !!option.children?.length;

  const searchValueNotEmpty = !!searchValue;

  const showSubOptions =
    showChevron && renderSubOption && (isExpanded || searchValueNotEmpty);

  const isMultiOptionSelected = useMemo(
    () => Array.isArray(value) && value.find((v) => v === option.value),
    [value]
  );

  const handleChevronClick = (e: SyntheticEvent) => {
    e.stopPropagation();
    setExpanded((prev) => !prev);
  };

  const handleFavoriteClick = (e: SyntheticEvent) => {
    if (toggleFavorite) {
      e.stopPropagation();
      toggleFavorite(option.value as string, !option.isFavorite);
    }
  };

  const renderItem = (option: ISelectOption<T, P>, depth = 1) => (
    <div
      onClick={() => handleClick(option.value, option)}
      style={{ paddingLeft: 16 * depth }}
      className={cn('select__options__item__label', {
        select__options__item__label__selected: value === option.value,
      })}
    >
      <div>
        <Highlighter
          highlightClassName="highlighted tpg-c2"
          unhighlightClassName="unhighlighted tpg-c2 break-all"
          searchWords={[searchValue]}
          autoEscape={true}
          textToHighlight={option.label}
        />
      </div>
      <div className="select__body__icon flex items-center">
        {enableFavorites && (
          <div className="icon-container">
            {option.isFavorite ? (
              <StarActiveIcon
                className="star-active-icon"
                onClick={handleFavoriteClick}
              />
            ) : (
              <StarPassiveIcon
                className="star-passive-icon"
                onClick={handleFavoriteClick}
              />
            )}
          </div>
        )}
        {showChevron && (
          <div className="icon-container">
            <ChevronDown
              className={cn('chevron', {
                chevron_up: isExpanded,
              })}
              onClick={handleChevronClick}
            />
          </div>
        )}
        {isMultiple && (
          <div
            className={cn(
              'h-2 w-2 border-solid border-[1px] rounded-[8px]',
              isMultiOptionSelected
                ? 'border-bright_product bg-bright_product'
                : 'border-tpg_light'
            )}
          />
        )}
      </div>
    </div>
  );

  return (
    <li
      id={`option-${option.value}-${idx}`}
      className={cn('select__options__item', {
        'select__options__item__sub-item': option.children?.length,
      })}
    >
      {renderItem(option, depth)}
      {showSubOptions && (
        <ul className="select__options_sublayer">
          {option.children?.map(renderSubOption(depth + 1))}
        </ul>
      )}
    </li>
  );
};

export default SelectItem;
